import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import type { CompanyDetail } from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { Overlay } from "../ui/Overlay";

/**
 * Archiving a company from its own page, and the banner an archived one shows.
 *
 * The CRM's delete: the company leaves every list, search and dashboard, its
 * people go back to the unfiled queue, and nothing is lost -- an admin can
 * restore it, and the restore files those people back. Said in the dialog,
 * because "archive" means three different things in three other products.
 */

/** Why the button is off for this company, or null when it can be archived. */
export function archiveBlockedReason(company: CompanyDetail): string | null {
  if (company.external) {
    return `Linked to ${company.external.source} account ${company.external.id}; its sync would put it straight back. Stop syncing it there first.`;
  }
  if (company.branchCount > 0) {
    return "It has branches. Move or archive them first.";
  }
  return null;
}

export function ArchiveDialog({
  company,
  onClose,
}: {
  company: CompanyDetail;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const archive = useMutation({
    mutationFn: () => crmApi.archiveCompany(company.id),
    onSuccess: () => {
      // Everything: the page, the list it leaves, search, the queue, the dashboards.
      void queryClient.invalidateQueries({ queryKey: ["crm"] });
      onClose();
    },
  });

  const people = company.contacts.length;

  return (
    <Overlay
      onClose={onClose}
      size="sm"
      title={`Archive ${company.name}?`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={archive.isPending}
            onClick={() => archive.mutate()}
          >
            {archive.isPending ? "Archiving…" : "Archive"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2 text-sm text-content">
        <p>
          It leaves the company list, search, the duplicate queue and every
          dashboard, and new mail from its domain is no longer filed to it.
        </p>
        {people > 0 ? (
          <p>
            Its {people === 1 ? "contact goes" : `${people} contacts go`} back to
            the unfiled queue, with their tickets and history.
          </p>
        ) : null}
        <p className="text-muted">
          Nothing is deleted. An admin can restore it from Settings → CRM →
          Tidy up, and restoring files its people back.
        </p>
        {archive.isError ? (
          <p className="text-xs text-sla-breached">
            {archive.error instanceof ApiError
              ? archive.error.message
              : "Could not archive it."}
          </p>
        ) : null}
      </div>
    </Overlay>
  );
}

/** Across the top of an archived company's page, with the way back for an admin. */
export function ArchivedBanner({
  company,
  isAdmin,
}: {
  company: CompanyDetail;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();

  const restore = useMutation({
    mutationFn: () => crmApi.restoreCompany(company.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["crm"] });
    },
  });

  if (!company.archivedAt) return null;

  const when = new Date(company.archivedAt).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface-2 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-content">
          Archived {when}
          {company.archivedBy ? ` by ${company.archivedBy.name}` : ""}.
        </p>
        <p className="text-[11px] text-muted">
          It is out of every list and search, and takes no changes, people or
          activity until it is restored.
        </p>
        {restore.isError ? (
          <p className="text-[11px] text-sla-breached">
            {restore.error instanceof ApiError
              ? restore.error.message
              : "Could not restore it."}
          </p>
        ) : null}
      </div>
      {isAdmin ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={restore.isPending}
          onClick={() => restore.mutate()}
        >
          {restore.isPending ? "Restoring…" : "Restore"}
        </Button>
      ) : null}
    </div>
  );
}
